import { addBrazeAlias, identifyBrazeUser, setBrazeEmailAndSubscriptions, startBrazeIfConsented, trackBrazeEvent } from './useBraze';
import type { SubscriptionState } from './index';
import type { Account } from '@/models/account/authModels';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { requestPushPermissionIfNeeded } from '@/services/pushNotificationService';

/**
 * Map user communication preferences to Braze subscription states
 */
export function mapPreferencesToBrazeSubscriptions(account: Account): {
    email?: SubscriptionState;
} {
    // User is opted in to email if ANY of these are true
    const emailOptedIn = account.receiveDonatosNewsAndEventEmails || account.ltdCommunicationEmail || account.lrmEmails || account.receivePersonalizedOffers || account.isMarketingEmail;

    // Only send OPTED_IN — never actively send UNSUBSCRIBED from a sync.
    // Unsubscribes must come from an explicit user action, not account flag evaluation.
    // Push subscription state is managed by Braze via device token registration.
    return {
        email: emailOptedIn ? 'OPTED_IN' : undefined,
    };
}

/**
 * Initialize Braze SDK on app launch and re-identify returning users.
 * syncUserToBraze only runs on explicit login, so returning users with persisted
 * sessions would never get changeUser called. We persist the customerId locally
 * to re-identify on every cold start without an extra API call.
 */
export async function initializeBraze(): Promise<void> {
    const hasConsent = true;
    await startBrazeIfConsented(hasConsent);

    if (Capacitor.isNativePlatform()) {
        const { value: customerId } = await Preferences.get({ key: 'braze_customer_id' });
        if (customerId) {
            // Check permissions BEFORE calling identifyBrazeUser so the push subscription
            // is passed atomically with changeUser(). Without this, changeUser() runs first
            // and Braze can sync from the server with UNSUBSCRIBED before the separate
            // setPushNotificationSubscriptionType call arrives.
            const permStatus = await PushNotifications.checkPermissions();
            const subs: { push?: SubscriptionState } | undefined = permStatus.receive === 'granted' ? { push: 'OPTED_IN' } : undefined;
            await identifyBrazeUser(customerId, undefined, subs);
        }
    }
}

/**
 * Sync user profile to Braze after login or profile update
 */
export async function syncUserToBraze(account: Account): Promise<void> {
    if (!account.customerId || !account.emailAddress) return;

    const subscriptions: { email?: SubscriptionState; push?: SubscriptionState } = mapPreferencesToBrazeSubscriptions(account);

    if (Capacitor.isNativePlatform()) {
        const permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'granted') {
            subscriptions.push = 'OPTED_IN';
        }
    }

    await identifyBrazeUser(account.customerId, account.emailAddress, subscriptions);
    await requestPushPermissionIfNeeded();
}

/**
 * Track order placement event
 */
export async function trackOrderPlaced(orderId: string, total: number, storeNumber?: string): Promise<void> {
    await trackBrazeEvent('order_placed', {
        order_id: orderId,
        total,
        store: storeNumber || 'Unknown',
    });
}

/**
 * Track user registration event
 */
export async function trackUserRegistered(customerId: string, email: string): Promise<void> {
    await trackBrazeEvent('user_registered', {
        customer_id: customerId,
        email,
    });
}

/**
 * Track communication preference updates
 */
export async function trackPreferenceUpdate(preferenceType: string, optedIn: boolean): Promise<void> {
    await trackBrazeEvent('preference_updated', {
        preference_type: preferenceType,
        opted_in: optedIn,
    });
}

/**
 * Attach a guest's email + marketing opt-in to the current anonymous Braze
 * profile. Does NOT call changeUser — same-device signup later merges this
 * anonymous profile into the identified one via changeUser(customerId).
 * Also tags the profile with an "email" user_alias so the server can call
 * Braze /users/identify after signup to merge cross-device guest profiles
 * into the identified customer.
 * Only call this when isMarketingEmail is true.
 */
export async function syncGuestEmailToBraze(email: string): Promise<void> {
    if (!email) return;
    const normalized = email.trim().toLowerCase();
    await addBrazeAlias(normalized, 'email');
    await setBrazeEmailAndSubscriptions(normalized, { email: 'OPTED_IN' });
}
